import type { Mesh, MeshCallbacks, MeshMessageType } from '../mesh.ts';
import type { Connect, Directory, Disconnect, ReplicaChange } from './directory.ts';
import { randomId } from '../common/ids.ts';
import { getLogger } from '../logger.ts';
import type { DomainInfo } from '../state/types/domain-registry.ts';
import type { WebsocketConnection, WebsocketConnectionFactory } from '../common/ws/types.ts';
import type { MessageReceiver } from '../common/channel.ts';
import { websocketConnectionFactory } from '../common/ws/factory.ts';
import type { Connection, Command, WsMeshServer } from './ws-mesh/types.ts';
import { createRelay } from './ws-mesh/relay.ts';
import * as compat from './compat.ts';
import { wrapWithHeaders, type WebSocketHeaderOptions } from './ws-headers.ts';

const logger = getLogger('gateway.mesh.ws');

type NodeConnections = {
    connections: Map<string, Connection>
    callbacks?: MeshCallbacks
}

function publishNodePeer(msg: compat.TransportMessage, from: string, node?: NodeConnections) {
    const {receiver, body} = msg;
    const to = receiver['node'];
    if (to) {
        const nodeConnection = node?.connections.get(to);
        if (nodeConnection) {
            nodeConnection.send({type: 'data', from, to, data: msg});
        }
    }
}

function publishMessage(msg: MeshMessageType, from: string, node?: NodeConnections) {
    const [receiverType, transportMessage] = compat.toTransportMessage(msg);
    switch (receiverType) {
        case "cluster": {
            node?.connections.forEach((connection, to) => {
                connection.send({type: 'data', from, to, data: transportMessage});
            });
            break;
        }
        case "node": // fall-through
        case "peer": {
            publishNodePeer(transportMessage, from, node);
            break;
        }
        default: {
            logger.error(`Unhandled receiver type when publishing ${JSON.stringify(msg)}`);
            break;
        }

    }
}

function closeConnections(connections: Map<string, Connection>)
{
    for (const connection of connections.values()) {
        connection.close();
    }
}

type ConnectingNodes = {type: 'connecting-nodes', from: string, to: string, connection: Connection};
type DisconnectingNodes = {type: 'disconnecting-nodes', from: string, to: string};
type Action
    = ConnectingNodes
    | DisconnectingNodes
    ;
type NodeConnected = { type: 'node-connected', from: string, to: string };
type NodeDisconnected = { type: 'node-disconnected', from: string, to: string };
type NodeData = { type: 'node-data', from: string, to: string, msg: compat.TransportMessage };
type Event
    = NodeConnected
    | NodeDisconnected
    | NodeData
    ;
type Message = Event | Action;
function directoryConnectNodes<T>(handler: MessageReceiver<Message>, msg: Connect, socket: { factory: WebsocketConnectionFactory<Command>,  URL?: typeof URL }) {
    const from = msg.node;
    const to = msg.to.node;
    const uri = msg.to.endpoint;
    if (logger.enabledFor('debug')) {
        logger.debug(`directory connect ${from} to ${to} via ${uri}`);
    }
    const connection = socket.factory.create(uri, undefined, { URL: socket.URL });
    connection.onConnect = () => {
        handler({type: 'node-connected', from, to});
    };
    connection.onReceive = (msg) => {
        if (msg.type === 'data') {
            handler({type: 'node-data', to: from, from: to, msg: msg.data });
        }
    };
    connection.onDisconnect = () => {
        handler({type: 'node-disconnected', from, to})
    };
    handler({type: 'connecting-nodes', from, to, connection});
    connection.connect();
}

function directoryDisconnectNodes(handler: MessageReceiver<Action>, msg: Disconnect) {
    const from = msg.node;
    const to = msg.from.node;
    if (logger.enabledFor('debug')) {
        logger.debug(`directory disconnect ${from} to ${to}`);
    }
    handler({type: 'disconnecting-nodes', from, to});
}

function directoryReplicaChange(_handler: MessageReceiver<Action>, msg: ReplicaChange, server: WsMeshServer) {
    if (logger.enabledFor('debug')) {
        logger.debug(`directory replica change for node ${msg.node}: ${JSON.stringify(msg.replicas)}`);
    }
    server.register(msg.node, msg.replicas);
}


class WebSocketCluster implements Mesh {
    readonly #localNodes: Map<string, NodeConnections>;
    readonly #server: WsMeshServer;
    readonly #directory: Directory;
    readonly #handler: MessageReceiver<Message>;
    readonly #connectionFactory: WebsocketConnectionFactory<Command>;
    readonly #URL?: typeof URL;
    constructor(localNodes: Map<string, NodeConnections>,
                server: WsMeshServer,
                directory: Directory,
                handler: MessageReceiver<Message>,
                connectionFactory: WebsocketConnectionFactory<Command>,
                opts?: { URL?: typeof URL }) {
        this.#localNodes = localNodes;
        this.#server = server;
        this.#directory = directory;
        this.#handler = handler;
        this.#connectionFactory = connectionFactory;
        this.#URL = opts?.URL;
    }

    register(nodeId: string | undefined, _gateway: { domains: Array<DomainInfo> }, callbacks?: MeshCallbacks): string {
        nodeId = nodeId ?? randomId();
        if (logger.enabledFor('info')) {
            logger.info(`registering local node ${nodeId}`);
        }
        this.#localNodes.set(nodeId, { callbacks, connections: new Map<string, WebsocketConnection<Command>>() });
        this.#directory.add(nodeId, undefined, {
            replicaChange: (m) => {
                directoryReplicaChange(this.#handler, m, this.#server);
            },
            connect: (m) => {
                directoryConnectNodes(this.#handler, m, { factory: this.#connectionFactory, URL: this.#URL });
            },
            disconnect: (m) => {
                directoryDisconnectNodes(this.#handler, m);
            }
        });
        return nodeId;
    }

    unregister(nodeId: string) {
        this.#directory.remove(nodeId);
        const data = this.#localNodes.get(nodeId);
        if (data) {
            if (logger.enabledFor('info')) {
                logger.info(`unregistering local node ${nodeId}`);
            }
            this.#localNodes.delete(nodeId);
            closeConnections(data.connections);
        }
        this.#server.unregister(nodeId);
    }

    publish<T>(nodeId: string, message: MeshMessageType) {
        publishMessage(message, nodeId, this.#localNodes.get(nodeId));
    }

    addUsers(nodeId: string, added: string[]) {
        this.#directory.addUsers(nodeId, ...added);
    }

    removeUsers(nodeId: string, removed: string[]) {
        this.#directory.removeUsers(nodeId, ...removed);
    }

    close() {
        this.#directory.close();

        for (const [, data] of this.#localNodes) {
            closeConnections(data.connections);
        }
        this.#localNodes.clear();

        this.#server.stop();
        this.#connectionFactory.close();
    }
}

function handleConnectingNodes(localNodes: Map<string, NodeConnections>, msg: ConnectingNodes) {
    const {from: nodeId, to, connection} = msg;
    const node = localNodes.get(nodeId);
    if (node) {
        if (logger.enabledFor('debug')) {
            logger.debug(`processing connection request from ${to} to ${nodeId}`);
        }
        node.connections.set(to, connection);
    }
    else {
        logger.warn(`discarding connection request from ${to} to ${nodeId}`);
        connection.close();
    }
}

function handleDisconnectingNodes(localNodes: Map<string, NodeConnections>, msg: DisconnectingNodes) {
    const {from, to} = msg;
    const node = localNodes.get(from);
    if (node) {
        const connection = node.connections.get(to);
        if (connection) {
            if (logger.enabledFor('debug')) {
                logger.debug(`processing disconnection request from ${to} to ${from}`);
            }
            node.connections.delete(to);
            connection.close();
            return;
        }
    }
    logger.warn(`discarding disconnection request from ${to} to ${from}`);
}

function handleNodeConnected(localNodes: Map<string, NodeConnections>, msg: NodeConnected) {
    const {from, to} = msg;
    const node = localNodes.get(from);
    if (node) {
        const connection = node.connections.get(to);
        if (connection) {
            if (logger.enabledFor('info')) {
                logger.info(`mesh connected ${from} to ${to}, sending hello`);
            }
            connection.send({ type: 'hello', from, to });
            const cb = node.callbacks?.onMemberAdded;
            if (cb) {
                cb(from, to);
            }
        }
    }
}

function handleNodeDisconnected(localNodes: Map<string, NodeConnections>, msg: NodeDisconnected) {
    const {from, to} = msg;
    const node = localNodes.get(from);
    if (node) {
        if (logger.enabledFor('info')) {
            logger.info(`mesh disconnected ${from} from ${to}`);
        }

        const cb = node.callbacks?.onMemberRemoved;
        if (cb) {
            cb(from, to);
        }
    }
}

function handleNodeData(localNodes: Map<string, NodeConnections>, msg: NodeData) {
    const {from, to} = msg;
    const localNode = localNodes.get(to);
    if (localNode) {
        const connection = localNode.connections.get(from);
        if (connection) {
            const cb = localNode.callbacks?.onMessage;
            if (cb) {
                cb(to, compat.fromTransportMessage(msg.msg));
            }
        }
    }
}

export type WebSocketClusterOptions = WebSocketOptions & {};

export type WebSocketOptions = { WebSocket?: typeof WebSocket, URL?: typeof URL } & WebSocketHeaderOptions;

export function websocketCluster(config: { relays: string }, directory: Directory, opts?: WebSocketClusterOptions): Mesh {
    const localNodes = new Map<string, NodeConnections>();
    const handler = (msg: Message) => {
        if (msg) {
            if (logger.enabledFor('debug')) {
                logger.debug(`mesh processing command ${JSON.stringify(msg, (key, value) => {
                    if (key === 'connection') {
                        return undefined;
                    }
                    return value;
                }, '\t')}`);
            }
            switch (msg.type) {
                case 'connecting-nodes': {
                    handleConnectingNodes(localNodes, msg);
                    break;
                }
                case 'disconnecting-nodes': {
                    handleDisconnectingNodes(localNodes, msg)
                    break;
                }
                case 'node-connected': {
                    handleNodeConnected(localNodes, msg);
                    break;
                }
                case 'node-disconnected': {
                    handleNodeDisconnected(localNodes, msg);
                    break;
                }
                case 'node-data': {
                    handleNodeData(localNodes, msg);
                    break;
                }
            }
        }
    };
    const factory = wrapWithHeaders(websocketConnectionFactory<Command>({ codec: compat.codec<Command>(), WebSocket: opts?.WebSocket }), opts);
    const server = createRelay(factory, `${config.relays}`, {
        onConnect: (from, to, connection) => {
            if (logger.enabledFor('debug')) {
                logger.debug(`server relay connected ${from} to ${to}`);
            }
            handler({type: 'connecting-nodes', from, to, connection});
            handler({type: 'node-connected', from, to});
        },
        onDisconnect:(from, to) => {
            handler({type: 'disconnecting-nodes', from, to});
            handler({type: 'node-disconnected', from, to});
        },
        onReceive: (to, from, msg) => {
            handler({type: 'node-data', from, to, msg});
        }
    });

    return new WebSocketCluster(localNodes, server, directory, handler, factory, opts);
}
